import { NavigationRoutes } from "@/components/navigation/constants";
import { DialogType } from "@/enums/dialogs";
import { toast } from "@/hooks/use-toast";
import { RtoBulkScanReturn, RtoBulkScannedColisItem } from "@/interfaces/Return";
import {
  scanRtoBulkColis,
  validateRtoBulkPickup,
} from "@/services/rto-bulk-pickup.service";
import { closeDialog, openDialog } from "@/store/reducers/dialogReducer";
import { setRefreshing } from "@/store/reducers/refreshReducer";
import { RootState } from "@/store/store";
import {
  findColisInReturns,
  getTotalColisCount,
  isColisScanned,
  isScanSessionComplete,
} from "@/utils/rto-bulk-scan";
import { withRtoQuery, canAccessRtoBulkPickup } from "@/utils/return-rto";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export const useBulkPickUpValidation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const roles = useSelector((state: RootState) => state.authentication.roles);
  const isRefreshing = useSelector(
    (state: RootState) => state.refresh.isRefreshing
  );
  const isOpen = useSelector((state: RootState) => state.dialog.isOpen);

  const [colisList, setColisList] = useState<RtoBulkScanReturn[]>([]);
  const [scannedColisItems, setScannedColisItems] = useState<
    RtoBulkScannedColisItem[]
  >([]);
  const [operationId, setOperationId] = useState<number | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const isAllowedRole = canAccessRtoBulkPickup(roles);
  const isSessionStarted = operationId !== null;
  const scannedCount = scannedColisItems.length;
  const expectedColisCount = getTotalColisCount(colisList);
  const tourneeCount = colisList.length;

  const isValidationDisabled =
    !isSessionStarted ||
    !isScanSessionComplete(colisList, scannedColisItems) ||
    isValidating ||
    isScanning;

  const addScannedColis = (
    trackingCode: string,
    pool: RtoBulkScanReturn[]
  ): boolean => {
    const match = findColisInReturns(pool, trackingCode);
    if (!match) {
      toast({
        title: "Code colis invalide",
        description: "Ce colis n'existe pas dans la session de ramassage",
        variant: "destructive",
      });
      return false;
    }

    setScannedColisItems((prev) => [
      ...prev,
      {
        numTournee: match.return_.numTournee,
        sellerId: match.return_.sellerId,
        colisCode: trackingCode,
      },
    ]);

    toast({
      title: "Colis ajouté avec succès",
      description: `Tournée N° ${match.return_.numTournee} - ${match.return_.sellerName}`,
      variant: "success",
    });
    return true;
  };

  const handleScan = async (trackingCode: string): Promise<boolean> => {
    if (isColisScanned(scannedColisItems, trackingCode)) {
      toast({
        title: "Colis déjà scanné",
        variant: "destructive",
      });
      return false;
    }

    setIsScanning(true);
    try {
      if (!isSessionStarted) {
        const { data, error } = await scanRtoBulkColis(trackingCode);

        if (!data) {
          toast({
            title: "Erreur lors du scan",
            description: error,
            variant: "destructive",
          });
          return false;
        }

        const added = addScannedColis(trackingCode, data.list);
        if (!added) {
          return false;
        }

        setOperationId(data.operationId);
        setColisList(data.list);
        return true;
      }

      return addScannedColis(trackingCode, colisList);
    } finally {
      setIsScanning(false);
    }
  };

  const handleValidation = async () => {
    if (!operationId) {
      toast({
        title: "Erreur lors de la validation",
        description: "Opération de ramassage introuvable",
        variant: "destructive",
      });
      return;
    }

    if (!isOpen) {
      dispatch(openDialog(DialogType.PICKUP_CONFIRMATION_DIALOG));
      return;
    }

    try {
      dispatch(closeDialog());
      setIsValidating(true);
      const response = await validateRtoBulkPickup({
        operationId,
        colis: scannedColisItems,
      });

      if (response?.status === 200 && response.data.success === true) {
        toast({
          title: "Ramassage validé",
          variant: "success",
        });
        navigate(withRtoQuery(NavigationRoutes.DASHBOARD), { replace: true });
        dispatch(setRefreshing(!isRefreshing));
      } else {
        toast({
          title: "Erreur lors de la validation",
          description: response?.data?.message,
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Erreur lors de la validation",
        variant: "destructive",
      });
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    if (!isAllowedRole) {
      navigate(NavigationRoutes.NOT_AUTHORIZED, { replace: true });
    }
  }, [isAllowedRole, navigate]);

  return {
    isAllowedRole,
    colisList,
    scannedColisItems,
    scannedCount,
    expectedColisCount,
    tourneeCount,
    isSessionStarted,
    isScanning,
    isValidating,
    isValidationDisabled,
    handleScan,
    handleValidation,
  };
};
